import React, { useMemo } from 'react';
import CreatableSelect from 'react-select/creatable';
import { MultiValue } from 'react-select';
import { useQuery, useMutation } from '@apollo/client';

import {
  getTagsForZoneQuery,
  createTagMutation,
  updateTagMutation,
  deleteTagMutation,
} from '../../queries/tags';

import SimpleEditableMultiValueLabel from './MultiValueLabel';
import { useConfirmationModalContext } from '../../context/ConfirmationModalContext';

export interface Tag {
  _id: string;
  name: string;
  zoneId: string;
  color?: string;
}

interface TagOption {
  value: string;
  label: string;
  color?: string;
}

interface TagSelectProps {
  value: Tag[];
  onChange: (tags: Tag[]) => void;
  zoneId: string;
  placeholder?: string;
  disabled?: boolean;
  allowCreate?: boolean;
}

const toOption = (tag: Tag): TagOption => ({
  value: tag._id,
  label: tag.name,
  color: tag.color,
});

export const getContrastColor = (hexColor?: string) => {
  if (!hexColor) return '#000000';
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#FFFFFF';
};

/* ---------- Component ---------- */

export const TagSelect: React.FC<TagSelectProps> = ({
  value: selectedTags,
  onChange,
  zoneId,
  placeholder = 'Lägg till taggar...',
  disabled = false,
  allowCreate = true,
}) => {
  const confirmationModalContext = useConfirmationModalContext();

  /* ---------- Queries ---------- */

  const { data, loading: loadingSuggestions, refetch } = useQuery(getTagsForZoneQuery, {
    variables: { zoneId },
    skip: !zoneId,
  });

  const allTags: Tag[] = useMemo(() => data?.tags ?? [], [data?.tags]);

  const [createTag, { loading: creatingTag }] = useMutation(createTagMutation);
  const [updateTag, { loading: updatingTag }] = useMutation(updateTagMutation);
  const [deleteTag, { loading: deletingTag }] = useMutation(deleteTagMutation);

  const loading = loadingSuggestions || creatingTag || updatingTag || deletingTag;

  /* ---------- Derived data ---------- */

  const selectedOptions = useMemo(
    () => selectedTags.map(toOption),
    [selectedTags]
  );

  const suggestionOptions = useMemo(
    () =>
      allTags
        .filter(tag => !selectedTags.some(t => t._id === tag._id))
        .map(toOption),
    [allTags, selectedTags]
  );

  /* ---------- Handlers ---------- */

  const handleChange = (options: MultiValue<TagOption>) => {
    if (options === null) {
      onChange([]);
      return;
    }
    const nextTags = options
      .map(option => allTags.find(t => t._id === option.value))
      .filter(Boolean) as Tag[];

    onChange(nextTags);
  };

  const handleCreate = async (inputValue: string) => {
    if (!allowCreate || !zoneId || creatingTag) return;

    const name = inputValue.trim();
    if (!name) return;

    const existing = allTags.find(
      tag => tag.name.toLowerCase() === name.toLowerCase()
    );

    if (existing) {
      if (!selectedTags.some(t => t._id === existing._id)) {
        onChange([...selectedTags, existing]);
      }
      return;
    }

    try {
      const { data } = await createTag({
        variables: { name, zoneId },
      });

      const newTag: Tag = data.createTag;
      onChange([...selectedTags, newTag]);
      refetch();
    } catch (err) {
      console.error('Failed to create tag:', err);
    }
  };

  const handleTagRename = async (tagId: string, newName: string, newColor?: string) => {
    const name = newName.trim();
    if (!name) return;

    const currentTag = selectedTags.find(t => t._id === tagId);
    if (!currentTag) return;

    const duplicate = allTags.find(
      t => t._id !== tagId && t.name.toLowerCase() === name.toLowerCase()
    );

    if (duplicate) {
      const replaced = selectedTags.map(t =>
        t._id === tagId ? duplicate : t
      );
      onChange(replaced);
      return;
    }

    try {
      const { data } = await updateTag({
        variables: {
          _id: tagId,
          name,
          color: newColor || currentTag.color,
        },
      });

      const updatedTag: Tag = data.updateTag;

      onChange(
        selectedTags.map(t => (t._id === tagId ? updatedTag : t))
      );
      refetch();
    } catch (err) {
      console.error('Failed to update tag:', err);
    }
  };

  const handleTagDelete = async (tagId: string) => {
    const result = await confirmationModalContext.showConfirmation('Ta bort tagg', 'Är du säker på att du vill ta bort taggen? Taggen tas bort från alla företag som använder den i denna zon.');

    if (!result) return;
    try {
      await deleteTag({
        variables: { _id: tagId },
      });
      onChange(selectedTags.filter(t => t._id !== tagId));
      refetch();
    } catch (err) {
      console.error('Failed to delete tag:', err);
    }
  };

  /* ---------- Render ---------- */

  return (
    <CreatableSelect
      isMulti
      value={selectedOptions}
      options={suggestionOptions}
      onChange={handleChange}
      onCreateOption={allowCreate ? handleCreate : undefined}
      isDisabled={disabled}
      isLoading={loading}
      placeholder={placeholder}
      closeMenuOnSelect={false}
      noOptionsMessage={() =>
        allowCreate
          ? 'Skriv för att skapa ny tagg...'
          : 'Inga taggar tillgängliga'
      }
      formatCreateLabel={input => `Skapa "${input}"`}
      components={{
        MultiValueLabel: props => (
          <SimpleEditableMultiValueLabel
            props={props}
            onTagRename={handleTagRename}
            onDelete={handleTagDelete}
          />
        )
      }}
      styles={{
        multiValue: (base, state) => ({
          ...base,
          backgroundColor: state.data.color || '#e9ecef',
          borderRadius: '4px',
        }),
        multiValueLabel: (base, state) => ({
          ...base,
          color: getContrastColor(state.data.color),
          padding: '2px 6px',
        }),
        multiValueRemove: (base, state) => ({
          ...base,
          'color': getContrastColor(state.data.color),
          'cursor': 'pointer',
          'padding': '2px 6px',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.1)',
          },
        }),
      }}
    />
  );
};


